import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { slurpGroups } from "../../lib/slurpGroupsApi";
import { AuthorBlock, SlurpLoader } from "./HolderGroupShared";
import { useConfirmDialog } from "./ConfirmDialog";
import ImagePicker from "./ImagePicker";
import LikeButton from "./LikeButton";
import Linkify from "./Linkify";
import ImageLightbox from "./ImageLightbox";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const POLL_MS = 15_000;

export default function HolderGroupThread() {
  const { mint, id } = useParams();
  const wallet = useWallet();

  const [data, setData] = useState({ thread: null, comments: [] });
  const [membership, setMembership] = useState({ member: false, role: null, banned: false });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [replyTo, setReplyTo] = useState(null); // comment id we're replying to (null = top-level)
  const [editingOp, setEditingOp] = useState(false);
  const { confirm, alert: showAlert, dialog } = useConfirmDialog();

  const refresh = useCallback(async () => {
    try {
      const viewer = wallet?.publicKey?.toBase58();
      const r = await slurpGroups.thread(mint, id, { viewer });
      setData(r);
      if (wallet?.publicKey) {
        const m = await slurpGroups.membership(mint, wallet.publicKey.toBase58());
        setMembership(m);
      }
    } catch (e) {
      setErr(e?.message || "Couldn't load thread");
    } finally {
      setLoading(false);
    }
  }, [mint, id, wallet?.publicKey]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const viewer = wallet?.publicKey?.toBase58();
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      slurpGroups.thread(mint, id, { viewer }).then(setData).catch(() => {});
    };
    const timer = setInterval(tick, POLL_MS);
    return () => clearInterval(timer);
  }, [mint, id, wallet?.publicKey]);

  if (loading) return <SlurpLoader label="Loading thread" />;
  if (err || !data.thread) {
    return (
      <div className="pm-group__error">
        <div className="pm-group__error-text">{err || "Thread not found"}</div>
        <Link to={`/groups/${mint}`} className="pm-group__error-back">← Back to group</Link>
      </div>
    );
  }

  // Threads are members-only — gate the entire detail page so non-members
  // can't read the OP, comments, or poll content even with a direct link.
  if (!membership.member) {
    return (
      <div className="pm-group">
        <div className="pm-group__locked pm-group__locked--full">
          <div className="pm-group__locked-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Members only
          </div>
          <p className="pm-group__locked-text">
            {membership.banned
              ? "You've been banned from this group."
              : "Join the group to read threads and replies."}
          </p>
          <Link to={`/groups/${mint}`} className="pm-group__sticker pm-group__sticker--accent" style={{ marginTop: 12 }}>
            ← back to group
          </Link>
        </div>
      </div>
    );
  }

  const t = data.thread;
  const isAdmin = membership.role === "creator" || membership.role === "admin";
  const isAuthor = wallet?.publicKey?.toBase58() === t.author;

  const togglePin = async () => {
    try {
      await slurpGroups.pinThread({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        threadId: t.id,
        pinned: !t.pinned,
      });
      await refresh();
    } catch (e) { showAlert(e?.message || "Pin failed"); }
  };
  const hideThread = async () => {
    const ok = await confirm("Hide this thread from the group? Members won't see it anymore.", {
      title: "Hide thread?", confirmLabel: "Hide", danger: true,
    });
    if (!ok) return;
    try {
      await slurpGroups.hideThread({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        threadId: t.id,
      });
      window.location.href = `/#/groups/${mint}`;
    } catch (e) { showAlert(e?.message || "Hide failed"); }
  };
  const deleteThread = async () => {
    const ok = await confirm("This can't be undone.", {
      title: "Delete thread?", confirmLabel: "Delete", danger: true,
    });
    if (!ok) return;
    try {
      await slurpGroups.deleteThread({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        threadId: t.id,
      });
      window.location.href = `/#/groups/${mint}`;
    } catch (e) { showAlert(e?.message || "Delete failed"); }
  };
  const flagThread = async () => {
    const ok = await confirm("Report this thread for the group's admins to review?", {
      title: "Report thread?", confirmLabel: "Report", danger: true,
    });
    if (!ok) return;
    try {
      await slurpGroups.flagThread({
        mint, wallet: wallet.publicKey, signMessage: wallet.signMessage, threadId: t.id,
      });
      showAlert("Thanks. The admins have been notified.");
    } catch (e) { showAlert(e?.message || "Flag failed"); }
  };

  // Build a tree: top-level comments + replies grouped under parent.
  const topLevel = data.comments.filter((c) => c.parent_id == null);
  // Quick lookup by id so a nested reply can render a faded quote
  // block showing the comment it's replying to.
  const commentsById = new Map(data.comments.map((c) => [c.id, c]));
  const repliesByParent = {};
  for (const c of data.comments) {
    if (c.parent_id != null) {
      const k = String(c.parent_id);
      (repliesByParent[k] = repliesByParent[k] || []).push(c);
    }
  }

  return (
    <div className="pm-group">
      {dialog}
      <div className="pm-group__threadwrap">
        <Link to={`/groups/${mint}`} className="pm-group__hero-back">← back to group</Link>

        <article className="pm-group__op">
          {t.pinned && <span className="pm-group__pin-badge">PINNED</span>}
          {!editingOp && (isAuthor || isAdmin) && (
            <div className="pm-group__op-admin">
              {isAuthor && !t.poll_options && (
                <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={() => setEditingOp(true)}>
                  edit
                </button>
              )}
              {isAdmin && (
                <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={togglePin}>
                  {t.pinned ? "unpin" : "pin"}
                </button>
              )}
              {(isAuthor || isAdmin) && (
                <button type="button" className="pm-group__sticker pm-group__sticker--leave" onClick={deleteThread}>
                  delete
                </button>
              )}
            </div>
          )}
          {editingOp ? (
            <EditThreadForm
              thread={t}
              mint={mint}
              wallet={wallet}
              onSaved={async () => { setEditingOp(false); await refresh(); }}
              onCancel={() => setEditingOp(false)}
            />
          ) : (
            <>
              <h1 className="pm-group__op-title">{t.title}</h1>
              <div className="pm-group__op-meta">
                <AuthorBlock
                  wallet={t.author}
                  username={t.author_username}
                  imageUrl={t.author_image_url}
                  role={t.author_role}
                />
                <span className="pm-group__thread-meta-dot">·</span>
                <span>{timeAgo(t.created_at)}</span>
                {t.edited_at && (
                  <>
                    <span className="pm-group__thread-meta-dot">·</span>
                    <span className="pm-group__edited">edited</span>
                  </>
                )}
              </div>
              {t.body && <p className="pm-group__op-body"><Linkify text={t.body} /></p>}
              {t.image_url && (
                <ImageLightbox src={t.image_url} className="pm-group__op-image" />
              )}
              {t.poll_options && (
                <PollDisplay
                  thread={t}
                  mint={mint}
                  wallet={wallet}
                  isMember={membership.member && !membership.banned}
                  onVoted={refresh}
                />
              )}
              <div className="pm-group__op-actions">
                <LikeButton
                  reaction={t.viewer_reaction}
                  likeCount={t.like_count}
                  dislikeCount={t.dislike_count}
                  disabled={!membership.member}
                  onToggle={(kind) => slurpGroups.reactThread({
                    mint,
                    wallet: wallet.publicKey,
                    signMessage: wallet.signMessage,
                    threadId: t.id,
                    kind,
                  })}
                />
                {!isAuthor && membership.member && (
                  <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={flagThread}>
                    report
                  </button>
                )}
              </div>
            </>
          )}
        </article>

        <h2 className="pm-group__section">{data.comments.length} {data.comments.length === 1 ? "reply" : "replies"}</h2>

        <ul className="pm-group__comments">
          {topLevel.map((c) => (
            <CommentNode
              key={c.id}
              c={c}
              repliesByParent={repliesByParent}
              commentsById={commentsById}
              depth={0}
              mint={mint}
              isAdmin={isAdmin}
              isMember={membership.member && !membership.banned}
              wallet={wallet}
              replyTo={replyTo}
              setReplyTo={setReplyTo}
              onChange={refresh}
              confirm={confirm}
              showAlert={showAlert}
            />
          ))}
        </ul>

        {membership.member && !membership.banned ? (
          <ReplyForm
            mint={mint}
            threadId={t.id}
            wallet={wallet}
            parentId={null}
            onPosted={refresh}
            placeholder="Write a reply…"
          />
        ) : (
          <div className="pm-group__reply-locked">
            {wallet?.publicKey ? "Join the group to reply." : "Connect your wallet to reply."}
          </div>
        )}
      </div>
    </div>
  );
}

// Cap visual indentation after this many levels — deeper replies still
// render and stay threaded by parent_id, but they stop indenting so a
// long chain doesn't push content off the right edge.
const MAX_INDENT_DEPTH = 5;

function CommentNode({ c, repliesByParent, commentsById, depth, mint, isAdmin, isMember, wallet, replyTo, setReplyTo, onChange, confirm, showAlert }) {
  const isReplyingHere = replyTo === c.id;
  const isAuthor = wallet?.publicKey?.toBase58() === c.author;
  const [editing, setEditing] = useState(false);
  const replies = repliesByParent[String(c.id)] || [];
  const hide = async () => {
    const ok = await confirm("Hide this comment from the thread?", {
      title: "Hide comment?", confirmLabel: "Hide", danger: true,
    });
    if (!ok) return;
    try {
      await slurpGroups.hideComment({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        commentId: c.id,
      });
      await onChange();
    } catch (e) { showAlert(e?.message || "Hide failed"); }
  };
  const del = async () => {
    const ok = await confirm("This can't be undone.", {
      title: "Delete comment?", confirmLabel: "Delete", danger: true,
    });
    if (!ok) return;
    try {
      await slurpGroups.deleteComment({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        commentId: c.id,
      });
      await onChange();
    } catch (e) { showAlert(e?.message || "Delete failed"); }
  };
  const flagIt = async () => {
    const ok = await confirm("Report this comment for the group's admins to review?", {
      title: "Report comment?", confirmLabel: "Report", danger: true,
    });
    if (!ok) return;
    try {
      await slurpGroups.flagComment({
        mint, wallet: wallet.publicKey, signMessage: wallet.signMessage, commentId: c.id,
      });
      showAlert("Thanks. The admins have been notified.");
    } catch (e) { showAlert(e?.message || "Flag failed"); }
  };
  return (
    <li className={`pm-group__comment${depth > 0 ? " is-reply" : ""}`}>
      <div className="pm-group__comment-head">
        <AuthorBlock
          wallet={c.author}
          username={c.author_username}
          imageUrl={c.author_image_url}
          role={c.author_role}
        />
        <span className="pm-group__comment-time">· {timeAgo(c.created_at)}</span>
        {c.edited_at && <span className="pm-group__edited">· edited</span>}
        <span className="pm-group__comment-spacer" />
        <LikeButton
          reaction={c.viewer_reaction}
          likeCount={c.like_count}
          dislikeCount={c.dislike_count}
          disabled={!isMember}
          onToggle={(kind) => slurpGroups.reactComment({
            mint,
            wallet: wallet.publicKey,
            signMessage: wallet.signMessage,
            commentId: c.id,
            kind,
          })}
        />
        {isMember && (
          <button
            type="button"
            className="pm-group__comment-action"
            onClick={() => setReplyTo(isReplyingHere ? null : c.id)}
          >
            {isReplyingHere ? "cancel" : "reply"}
          </button>
        )}
        {isAuthor && !editing && (
          <button type="button" className="pm-group__comment-action" onClick={() => setEditing(true)}>
            edit
          </button>
        )}
        {(isAuthor || isAdmin) && (
          <button type="button" className="pm-group__comment-action pm-group__comment-action--hide" onClick={del}>
            delete
          </button>
        )}
        {!isAuthor && isMember && (
          <button type="button" className="pm-group__comment-action" onClick={flagIt}>
            report
          </button>
        )}
      </div>
      {/* Quote-the-parent: faded recap of the comment this one is
        * replying to. Keeps conversation flow without scrolling up. */}
      {c.parent_id != null && commentsById?.get(c.parent_id) && (() => {
        const parent = commentsById.get(c.parent_id);
        const author = parent.author_username
          ? `@${parent.author_username}`
          : `${(parent.author || "").slice(0, 4)}…${(parent.author || "").slice(-4)}`;
        const body = (parent.body || "").trim();
        if (!body) return null;
        return (
          <div className="pm-group__comment-quote">
            <span className="pm-group__comment-quote-author">{author}</span>
            <span className="pm-group__comment-quote-body">{body}</span>
          </div>
        );
      })()}
      {editing ? (
        <EditCommentForm
          comment={c}
          mint={mint}
          wallet={wallet}
          onSaved={async () => { setEditing(false); await onChange(); }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          {c.body && <p className="pm-group__comment-body"><Linkify text={c.body} /></p>}
          {c.image_url && <ImageLightbox src={c.image_url} className="pm-group__comment-image" />}
        </>
      )}

      {isReplyingHere && (
        <div className="pm-group__inline-reply">
          <ReplyForm
            mint={mint}
            threadId={c.thread_id}
            wallet={wallet}
            parentId={c.id}
            onPosted={async () => { await onChange(); setReplyTo(null); }}
            placeholder={`Reply to ${c.author_username ? `@${c.author_username}` : "this comment"}…`}
          />
        </div>
      )}

      {replies.length > 0 && (
        <ul className={`pm-group__replies${depth >= MAX_INDENT_DEPTH ? " is-flat" : ""}`}>
          {replies.map((r) => (
            <CommentNode
              key={r.id}
              c={r}
              repliesByParent={repliesByParent}
              commentsById={commentsById}
              depth={depth + 1}
              mint={mint}
              isAdmin={isAdmin}
              isMember={isMember}
              wallet={wallet}
              replyTo={replyTo}
              setReplyTo={setReplyTo}
              onChange={onChange}
              confirm={confirm}
              showAlert={showAlert}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function PollDisplay({ thread, mint, wallet, isMember, onVoted }) {
  const [busy, setBusy] = useState(false);
  const options = Array.isArray(thread.poll_options) ? thread.poll_options : [];
  const closesAt = thread.poll_closes_at ? new Date(thread.poll_closes_at).getTime() : null;
  const isClosed = closesAt !== null && closesAt <= Date.now();
  const myVote = thread.viewer_vote != null ? Number(thread.viewer_vote) : null;
  const totalVotes = Number(thread.poll_vote_total || 0);

  // Build per-option counts from the parallel arrays the API returns.
  const counts = new Array(options.length).fill(0);
  const idxs = Array.isArray(thread.poll_vote_idxs) ? thread.poll_vote_idxs : [];
  const cnts = Array.isArray(thread.poll_vote_cnts) ? thread.poll_vote_cnts : [];
  for (let i = 0; i < idxs.length; i++) {
    const idx = Number(idxs[i]);
    if (idx >= 0 && idx < counts.length) counts[idx] = Number(cnts[i]) || 0;
  }

  const showResults = myVote !== null || isClosed;
  const closesLabel = (() => {
    if (!closesAt) return null;
    if (isClosed) return "Poll closed";
    const ms = closesAt - Date.now();
    const h = Math.floor(ms / 3600_000);
    const m = Math.floor((ms % 3600_000) / 60_000);
    if (h >= 24) return `Closes in ${Math.floor(h / 24)}d ${h % 24}h`;
    if (h > 0) return `Closes in ${h}h ${m}m`;
    return `Closes in ${m}m`;
  })();

  const cast = async (i) => {
    if (busy || isClosed) return;
    if (!wallet?.publicKey || !wallet?.signMessage) {
      alert("Connect your wallet to vote.");
      return;
    }
    if (!isMember) {
      alert("Join the group to vote.");
      return;
    }
    setBusy(true);
    try {
      await slurpGroups.vote({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        threadId: thread.id,
        option_index: i,
      });
      await onVoted();
    } catch (err) {
      alert(err?.message || "Couldn't vote");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pm-group__poll">
      <div className="pm-group__poll-options">
        {options.map((opt, i) => {
          const c = counts[i];
          const pct = totalVotes > 0 ? Math.round((c / totalVotes) * 100) : 0;
          const isMine = myVote === i;
          if (showResults) {
            return (
              <div
                key={i}
                className={`pm-group__poll-result${isMine ? " is-mine" : ""}${isClosed && c === Math.max(...counts) && c > 0 ? " is-winner" : ""}`}
              >
                <span className="pm-group__poll-result-bar" style={{ width: `${pct}%` }} aria-hidden="true" />
                <span className="pm-group__poll-result-label">
                  {isMine && <span className="pm-group__poll-mine">✓</span>}
                  {opt}
                </span>
                <span className="pm-group__poll-result-pct">{pct}% · {c}</span>
              </div>
            );
          }
          return (
            <button
              key={i}
              type="button"
              className="pm-group__poll-vote-btn"
              disabled={busy || !isMember}
              onClick={() => cast(i)}
            >
              {opt}
            </button>
          );
        })}
      </div>
      <div className="pm-group__poll-foot">
        <span>{totalVotes} {totalVotes === 1 ? "vote" : "votes"}</span>
        {closesLabel && (
          <>
            <span className="pm-group__thread-meta-dot">·</span>
            <span>{closesLabel}</span>
          </>
        )}
        {!showResults && !isMember && wallet?.publicKey && (
          <>
            <span className="pm-group__thread-meta-dot">·</span>
            <span>Join the group to vote</span>
          </>
        )}
        {!showResults && !wallet?.publicKey && (
          <>
            <span className="pm-group__thread-meta-dot">·</span>
            <span>Connect wallet to vote</span>
          </>
        )}
      </div>
    </div>
  );
}

function ReplyForm({ mint, threadId, wallet, parentId, onPosted, placeholder }) {
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!wallet?.publicKey || !wallet?.signMessage) return;
    if (!body.trim() && !imageUrl) return;
    setBusy(true);
    try {
      await slurpGroups.postComment({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        threadId,
        body: body.trim(),
        image_url: imageUrl || null,
        parent_id: parentId,
      });
      setBody("");
      setImageUrl("");
      await onPosted();
    } catch (err) { alert(err?.message || "Couldn't reply"); }
    finally { setBusy(false); }
  };
  return (
    <form className="pm-group__reply" onSubmit={submit}>
      <textarea
        className="pm-group__reply-body"
        placeholder={placeholder}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={250}
        rows={3}
      />
      <ImagePicker value={imageUrl} onChange={setImageUrl} prefix="groups/comments" disabled={busy} />
      <div className="pm-group__reply-actions">
        <span className="pm-group__compose-count">{body.length}/250</span>
        <button
          type="submit"
          className="pm-group__sticker pm-group__sticker--accent"
          disabled={busy || (!body.trim() && !imageUrl)}
        >
          {busy ? "posting…" : "post reply"}
        </button>
      </div>
    </form>
  );
}

function EditThreadForm({ thread, mint, wallet, onSaved, onCancel }) {
  const [title, setTitle] = useState(thread.title || "");
  const [body, setBody] = useState(thread.body || "");
  const [imageUrl, setImageUrl] = useState(thread.image_url || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await slurpGroups.editThread({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        threadId: thread.id,
        title: title.trim(),
        body: body.trim(),
        image_url: imageUrl || null,
      });
      onSaved();
    } catch (err) {
      alert(err?.message || "Couldn't save");
    } finally { setBusy(false); }
  };
  return (
    <form className="pm-group__compose" onSubmit={submit}>
      <input
        type="text"
        className="pm-group__compose-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={250}
        required
      />
      <textarea
        className="pm-group__compose-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={250}
        rows={4}
      />
      <ImagePicker value={imageUrl} onChange={setImageUrl} prefix="groups/threads" disabled={busy} />
      <div className="pm-group__compose-actions">
        <span className="pm-group__compose-count">{body.length}/250</span>
        <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={onCancel}>cancel</button>
        <button type="submit" className="pm-group__sticker pm-group__sticker--accent" disabled={busy || !title.trim()}>
          {busy ? "saving…" : "save"}
        </button>
      </div>
    </form>
  );
}

function EditCommentForm({ comment, mint, wallet, onSaved, onCancel }) {
  const [body, setBody] = useState(comment.body || "");
  const [imageUrl, setImageUrl] = useState(comment.image_url || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && !imageUrl) return;
    setBusy(true);
    try {
      await slurpGroups.editComment({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        commentId: comment.id,
        body: body.trim(),
        image_url: imageUrl || null,
      });
      onSaved();
    } catch (err) {
      alert(err?.message || "Couldn't save");
    } finally { setBusy(false); }
  };
  return (
    <form className="pm-group__reply" onSubmit={submit}>
      <textarea
        className="pm-group__reply-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={250}
        rows={3}
      />
      <ImagePicker value={imageUrl} onChange={setImageUrl} prefix="groups/comments" disabled={busy} />
      <div className="pm-group__reply-actions">
        <span className="pm-group__compose-count">{body.length}/250</span>
        <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={onCancel}>cancel</button>
        <button type="submit" className="pm-group__sticker pm-group__sticker--accent" disabled={busy || (!body.trim() && !imageUrl)}>
          {busy ? "saving…" : "save"}
        </button>
      </div>
    </form>
  );
}
